import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Calendar, Plus, Save, Edit, Trash2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisitLogSection = ({ patientId }) => {
  const [visitLogs, setVisitLogs] = useState([]);
  const [showAllVisits, setShowAllVisits] = useState(false);
  const [addingVisitLog, setAddingVisitLog] = useState(false);
  const [editingVisitLog, setEditingVisitLog] = useState(null);
  const [visitLogData, setVisitLogData] = useState({ date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    fetchVisitLogs();
  }, [patientId]);

  const fetchVisitLogs = async () => {
    try {
      const response = await axios.get(`${API}/patients/${patientId}/visit-logs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setVisitLogs(response.data);
    } catch (error) {
      console.error('Error fetching visit logs:', error);
      toast.error('Failed to load visit logs');
    }
  };

  const handleAddVisitLog = async () => {
    if (!visitLogData.notes.trim()) {
      toast.error('Please enter visit notes');
      return;
    }
    try {
      await axios.post(
        `${API}/patients/${patientId}/visit-logs`,
        { visit_date: visitLogData.date, notes: visitLogData.notes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Visit log added successfully');
      setVisitLogData({ date: new Date().toISOString().split('T')[0], notes: '' });
      setAddingVisitLog(false);
      fetchVisitLogs();
    } catch (error) {
      console.error('Error adding visit log:', error);
      toast.error('Failed to add visit log');
    }
  };

  const handleUpdateVisitLog = async () => {
    if (!visitLogData.notes.trim()) {
      toast.error('Please enter visit notes');
      return;
    }
    try {
      await axios.put(
        `${API}/patients/${patientId}/visit-logs/${editingVisitLog}`,
        { visit_date: visitLogData.date, notes: visitLogData.notes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Visit log updated successfully');
      setVisitLogData({ date: new Date().toISOString().split('T')[0], notes: '' });
      setEditingVisitLog(null);
      fetchVisitLogs();
    } catch (error) {
      console.error('Error updating visit log:', error);
      toast.error('Failed to update visit log');
    }
  };

  const handleDeleteVisitLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this visit log?')) return;
    try {
      await axios.delete(
        `${API}/patients/${patientId}/visit-logs/${logId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Visit log deleted successfully');
      fetchVisitLogs();
    } catch (error) {
      console.error('Error deleting visit log:', error);
      toast.error('Failed to delete visit log');
    }
  };

  const startEditVisitLog = (log) => {
    setEditingVisitLog(log.id);
    setVisitLogData({ date: log.date, notes: log.notes });
    setAddingVisitLog(false);
  };

  const cancelVisitLogEdit = () => {
    setEditingVisitLog(null);
    setAddingVisitLog(false);
    setVisitLogData({ date: new Date().toISOString().split('T')[0], notes: '' });
  };

  return (
    <div className="card mb-6" data-testid="visit-log-section">
      <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-purple-100">
        <h2 className="text-2xl font-semibold text-purple-900 flex items-center gap-2">
          <ClipboardList size={24} className="text-purple-600" />
          Visit Log
        </h2>
        {!addingVisitLog && !editingVisitLog && (
          <Button
            onClick={() => setAddingVisitLog(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="add-visit-log-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Visit Log
          </Button>
        )}
      </div>

      {/* Add/Edit Visit Log Form */}
      {(addingVisitLog || editingVisitLog) && (
        <div className="mb-6 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
          <h3 className="font-semibold text-purple-900 mb-3">
            {editingVisitLog ? 'Edit Visit Log' : 'New Visit Log'}
          </h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="visit-date">Visit Date</Label>
              <Input
                id="visit-date"
                type="date"
                value={visitLogData.date}
                onChange={(e) => setVisitLogData({ ...visitLogData, date: e.target.value })}
                className="mt-1"
                data-testid="visit-log-date-input"
              />
            </div>
            <div>
              <Label htmlFor="visit-notes">Visit Notes</Label>
              <Textarea
                id="visit-notes"
                value={visitLogData.notes}
                onChange={(e) => setVisitLogData({ ...visitLogData, notes: e.target.value })}
                rows={6}
                placeholder="Enter notes from this visit (visible to patient)..."
                className="mt-1"
                data-testid="visit-log-notes-textarea"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={editingVisitLog ? handleUpdateVisitLog : handleAddVisitLog}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="visit-log-save-btn"
              >
                <Save size={16} className="mr-2" />
                {editingVisitLog ? 'Update' : 'Add'} Visit Log
              </Button>
              <Button
                onClick={cancelVisitLogEdit}
                variant="outline"
                className="border-slate-300"
                data-testid="visit-log-cancel-btn"
              >
                <X size={16} className="mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Visit Logs List */}
      {visitLogs.length === 0 ? (
        <div className="text-center py-8">
          <ClipboardList size={48} className="mx-auto text-purple-300 mb-3" />
          <p className="text-slate-500 text-sm">No visit logs yet. Click &ldquo;Add Visit Log&rdquo; to create the first entry.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visitLogs.slice(0, showAllVisits ? visitLogs.length : 3).map((log, index) => (
            <div
              key={log.id}
              className="p-4 bg-white rounded-lg border-2 border-purple-100 hover:border-purple-200 transition-colors"
              data-testid={`visit-log-entry-${index}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <Calendar size={16} className="text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900">
                    {new Date(log.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                  {index === 0 && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      Most Recent
                    </span>
                  )}
                </div>
                {!addingVisitLog && !editingVisitLog && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => startEditVisitLog(log)}
                      size="sm"
                      variant="outline"
                      className="border-slate-300"
                      data-testid={`visit-log-edit-btn-${index}`}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteVisitLog(log.id)}
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`visit-log-delete-btn-${index}`}
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>

              <div className="mb-2">
                <p className="text-xs text-slate-500">Provider</p>
                <p className="text-sm font-medium text-slate-700">{log.provider_name}</p>
              </div>

              <div className="mt-3 pt-3 border-t border-purple-100">
                <p className="text-xs text-slate-500 mb-1">Visit Notes</p>
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 p-3 rounded">
                  {log.notes}
                </div>
              </div>

              <div className="mt-2 text-xs text-slate-400">
                Last updated: {new Date(log.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          ))}

          {visitLogs.length > 3 && (
            <button
              onClick={() => setShowAllVisits(!showAllVisits)}
              className="w-full py-3 text-sm font-medium text-purple-700 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors flex items-center justify-center gap-2 border-2 border-purple-200 hover:border-purple-300"
              data-testid="visit-log-toggle-all-btn"
            >
              {showAllVisits ? (
                <>
                  <ChevronUp size={18} />
                  Show Recent Visits Only ({visitLogs.slice(0, 3).length} of {visitLogs.length})
                </>
              ) : (
                <>
                  <ChevronDown size={18} />
                  Show All Visits ({visitLogs.length} total)
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitLogSection;
